import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { PartyPopper } from 'lucide-react'
import { cn } from '@/lib/utils'
import { padNumber } from '@/lib/photos'
import { HERO } from '@/constants/birthday'

const TARGET = Date.parse(HERO.birthday)
const DAY = 86400000

interface CountdownProps {
    className?: string
}

// Counts down to 5 November in India time, then switches to the birthday message.
const Countdown: FC<CountdownProps> = ({ className }): ReactNode => {
    const [now, setNow] = useState<number | null>(null)

    useEffect(() => {
        const tick = (): void => setNow(Date.now())

        tick()

        const timer = window.setInterval(tick, 1000)

        return () => window.clearInterval(timer)
    }, [])

    const remaining = now === null ? null : Math.max(0, TARGET - now)

    if (remaining === 0) {
        return (
            <div className={cn('inline-flex items-center gap-3 rounded-3xl border border-primary/15 bg-white/80 px-6 py-4 shadow-sm backdrop-blur', className)}>
                <PartyPopper className='size-7 shrink-0 text-primary' />
                <p className='font-script text-4xl leading-none text-primary sm:text-5xl'>
                    {HERO.todayTitle}
                </p>
            </div>
        )
    }

    const values = remaining === null
        ? null
        : [
            Math.floor(remaining / DAY),
            Math.floor(remaining / 3600000) % 24,
            Math.floor(remaining / 60000) % 60,
            Math.floor(remaining / 1000) % 60
        ]

    return (
        <div className={className}>
            <p className='text-base font-bold text-foreground/70'>
                {HERO.countdownTitle}
            </p>

            <div role='timer' aria-label={HERO.countdownTitle} className='mt-4 flex justify-center gap-2.5 sm:gap-4 lg:justify-start'>
                {HERO.units.map((unit, index) => (
                    <div
                        key={unit}
                        className='w-[4.4rem] rounded-2xl border border-primary/15 bg-white/80 px-1 py-3 text-center shadow-sm backdrop-blur sm:w-20'
                    >
                        <span className='block font-heading text-3xl font-semibold tabular-nums sm:text-4xl'>
                            {values ? padNumber(values[index]) : '--'}
                        </span>

                        <span className='mt-1 block text-[0.7rem] font-bold uppercase tracking-wider text-muted-foreground'>
                            {unit}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    )
}

export default Countdown
